/**
 * The flagship: Mahjong, on a jade table, above the fold.
 *
 * The headline is the question a beginner actually has, and the answer is a
 * button. Everything else in the band exists to make the ladder legible -
 * Belajar, Solo, Online - so a visitor knows there is a path and not just a
 * game they will lose at.
 */

import { useI18n } from "../../i18n";
import { TileStage } from "../art/TileStage";

const LADDER = ["learn", "solo", "online"] as const;

export interface HeroSectionProps {
  onPlay: () => void;
  onLearn: () => void;
}

export function HeroSection({ onPlay, onLearn }: HeroSectionProps) {
  const { t } = useI18n();

  return (
    <section className="hero" id="hero" aria-labelledby="hero-title">
      <div className="hero__inner">
        <div className="hero__copy">
          <p className="eyebrow eyebrow--onjade">
            <span className="eyebrow__dot" aria-hidden="true" />
            {t("portal.hero.eyebrow")}
          </p>
          <h1 className="hero__title" id="hero-title">
            <span className="hero__line hero__line--a">{t("portal.hero.titleA")}</span>
            <span className="hero__line hero__line--b">{t("portal.hero.titleB")}</span>
          </h1>
          <p className="hero__body">{t("portal.hero.body")}</p>

          <ol className="ladder" aria-label={t("portal.ladder.label")}>
            {LADDER.map((step) => (
              <li key={step} className="ladder__step" data-step={step}>
                <span className="ladder__name">{t(`portal.ladder.${step}.name`)}</span>
                <span className="ladder__desc">{t(`portal.ladder.${step}.desc`)}</span>
              </li>
            ))}
          </ol>

          <div className="hero__actions">
            <button
              type="button"
              className="btn btn--primary btn--lg"
              data-action="play-now"
              onClick={onPlay}
            >
              {t("portal.hero.play")}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--lg btn--onjade"
              data-action="how-to-play"
              onClick={onLearn}
            >
              {t("portal.hero.how")}
            </button>
          </div>
          <p className="hero__note">{t("portal.hero.note")}</p>
        </div>

        <div className="hero__stage">
          <TileStage label={t("portal.hero.artLabel")} />
        </div>
      </div>
    </section>
  );
}
